import React from 'react';
import CyberBadge from '../common/CyberBadge';
import { BadgeCheck, CheckCircle, XCircle, Mic, ExternalLink, Sparkles, MapPin, Radio, Shield } from 'lucide-react';

export default function ArtistsView({ artists = [], onApproveArtist, onRejectArtist }) {
  const pendingArtists = artists.filter((a) => !a.verified && a.status !== 'rejected');
  const verifiedArtists = artists.filter((a) => a.verified || a.status === 'approved');

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.02em', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <BadgeCheck size={28} style={{ color: 'var(--neon-cyan)' }} /> Creator Pro Verifications & Applications ({pendingArtists.length} Pending)
        </h1>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
          Review Creator Pro subscription applications, verify live GPS streaming permissions, and issue blue badges on Rap Map.
        </p>
      </div>

      {/* Pending Queue */}
      <div className="cyber-card cyber-card-glow-cyan" style={{ padding: '24px' }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Mic size={18} style={{ color: 'var(--neon-cyan)' }} /> Pending Verification & Upgrade Queue ({pendingArtists.length})
        </h3>

        {pendingArtists.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
            No pending creator verification or subscription requests at this time.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
            {pendingArtists.map((art) => (
              <div
                key={art._id}
                style={{
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid rgba(250, 204, 21, 0.4)',
                  borderRadius: 'var(--radius-md)',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <img
                    src={art.avatarUrl || art.profileImage || 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=200&q=80'}
                    alt={art.stageName || art.name}
                    style={{ width: '58px', height: '58px', borderRadius: '14px', objectFit: 'cover', border: '1.5px solid var(--neon-cyan)' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: 900, color: 'var(--text-primary)' }}>
                        {art.stageName || art.name || 'MC Rap Artist'}
                      </div>
                      <span className="badge badge-purple" style={{ fontSize: '0.65rem', fontWeight: 800 }}>
                        {art.plan || 'CREATOR PRO'}
                      </span>
                    </div>

                    <div style={{ fontSize: '0.8rem', color: 'var(--neon-cyan)', fontWeight: 800, marginTop: '2px' }}>
                      Primary Skill: {art.primarySkill || art.genre || 'RAPPER'}
                    </div>

                    <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <MapPin size={11} color="var(--neon-pink)" /> {art.city || 'Indore, MP'}
                    </div>
                  </div>
                </div>

                {art.bio && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.03)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
                    "{art.bio}"
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.75rem' }}>
                  <span style={{ color: 'var(--neon-emerald)', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 700 }}>
                    <Radio size={13} color="var(--neon-emerald)" /> GPS Streaming Enabled
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: 'auto' }}>
                  <button className="btn btn-emerald" style={{ fontSize: '0.78rem', padding: '10px', fontWeight: 800 }} onClick={() => onApproveArtist(art._id)}>
                    <CheckCircle size={14} /> Verify Creator Pro
                  </button>
                  <button className="btn btn-danger" style={{ fontSize: '0.78rem', padding: '10px', fontWeight: 800 }} onClick={() => onRejectArtist(art._id)}>
                    <XCircle size={14} /> Reject Application
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Verified Roster */}
      <div className="cyber-card" style={{ padding: '24px' }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Sparkles size={18} style={{ color: '#FACC15' }} /> Verified Creator Pro Roster ({verifiedArtists.length})
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {verifiedArtists.map((a) => (
            <div key={a._id} className="cyber-card" style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '10px', border: '1px solid rgba(0, 240, 255, 0.4)' }}>
              <BadgeCheck size={18} style={{ color: 'var(--neon-cyan)' }} />
              <div>
                <span style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-primary)' }}>{a.stageName || a.name}</span>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{a.primarySkill || 'CREATOR'} · {a.city || 'Indore'}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
